import React, { useState, useEffect } from 'react';
import apiClient from '../../utils/apiClient';
import { X, Coffee, Clock, Save, Plus, Trash2 } from 'lucide-react';

interface BreakTimeEditorV2Props {
  attendanceId: number;
  workDate: string;
  staffName: string;
  clockIn: string;
  clockOut: string | null;
  existingBreaks?: BreakTime[];
  onClose: () => void;
  onSave: () => void;
}

interface BreakTime {
  id?: number;
  start_time: string;
  end_time: string;
}

const BreakTimeEditorV2: React.FC<BreakTimeEditorV2Props> = ({
  attendanceId,
  workDate,
  staffName,
  clockIn,
  clockOut,
  existingBreaks = [],
  onClose,
  onSave
}) => {
  const [breaks, setBreaks] = useState<BreakTime[]>(
    existingBreaks.length > 0 ? existingBreaks : []
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 標準的な中抜けパターン
  const standardPatterns = [
    { label: '14:00-17:00 (標準)', start: '14:00', end: '17:00' },
    { label: '15:00-16:00 (短縮)', start: '15:00', end: '16:00' },
    { label: '14:00-16:00', start: '14:00', end: '16:00' },
    { label: '15:00-17:00', start: '15:00', end: '17:00' },
  ];

  const addBreak = (start: string = '14:00', end: string = '17:00') => {
    setBreaks([...breaks, { start_time: start, end_time: end }]);
  };

  const removeBreak = (index: number) => {
    setBreaks(breaks.filter((_, i) => i !== index));
  };

  const handleTimeChange = (index: number, type: 'start' | 'end', part: 'hour' | 'minute', value: string) => {
    const updated = [...breaks];
    const currentTime = updated[index][type === 'start' ? 'start_time' : 'end_time'];
    const [hour, minute] = currentTime.split(':');
    
    if (part === 'hour') {
      updated[index][type === 'start' ? 'start_time' : 'end_time'] = `${value}:${minute}`;
    } else {
      updated[index][type === 'start' ? 'start_time' : 'end_time'] = `${hour}:${value}`;
    }
    
    setBreaks(updated);
  };

  const applyPattern = (pattern: { start: string; end: string }) => {
    addBreak(pattern.start, pattern.end);
  };

  const calculateBreakDuration = (start: string, end: string) => {
    if (!start || !end) return '';
    
    const [startHour, startMin] = start.split(':').map(Number);
    const [endHour, endMin] = end.split(':').map(Number);
    
    const startMinutes = startHour * 60 + startMin;
    const endMinutes = endHour * 60 + endMin;
    const durationMinutes = endMinutes - startMinutes;
    
    if (durationMinutes <= 0) return '無効';
    
    const hours = Math.floor(durationMinutes / 60);
    const minutes = durationMinutes % 60;
    
    if (hours === 0) return `${minutes}分`;
    if (minutes === 0) return `${hours}時間`;
    return `${hours}時間${minutes}分`;
  };

  const handleSave = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // 中抜け時間を保存
      await apiClient.put(`/attendance/${attendanceId}/breaks`, {
        breaks: breaks.filter(b => b.start_time && b.end_time)
      });
      
      onSave();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.error || '保存に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (time: string) => {
    if (!time) return '';
    const date = new Date(time);
    return date.toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-2xl font-bold flex items-center gap-2">
              <Coffee className="text-orange-600" />
              中抜け時間の編集
            </h2>
            <p className="text-gray-600 mt-1">
              {staffName} - {new Date(workDate).toLocaleDateString('ja-JP')}
            </p>
            <p className="text-sm text-gray-500 mt-1">
              勤務時間: {formatTime(clockIn)} ～ {clockOut ? formatTime(clockOut) : '退勤未'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X size={24} />
          </button>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        {/* 標準パターン */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold mb-3">よく使うパターン</h3>
          <div className="grid grid-cols-2 gap-2">
            {standardPatterns.map((pattern, index) => (
              <button
                key={index}
                onClick={() => applyPattern(pattern)}
                className="px-4 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors text-sm"
              >
                {pattern.label}
              </button>
            ))}
          </div>
        </div>

        {/* 中抜け時間リスト */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold mb-3">中抜け時間</h3>
          {breaks.length === 0 ? (
            <p className="text-gray-500 text-center py-4">
              中抜け時間が設定されていません
            </p>
          ) : (
            <div className="space-y-4">
              {breaks.map((editBreak, index) => (
                <div key={index} className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Clock className="text-gray-400" size={20} />
                      <span className="font-medium">中抜け {index + 1}</span>
                      <span className="text-blue-600 font-bold">
                        ({calculateBreakDuration(editBreak.start_time, editBreak.end_time)})
                      </span>
                    </div>
                    <button
                      onClick={() => removeBreak(index)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 size={20} />
                    </button>
                  </div>

                  {/* 時間選択 */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        開始時刻
                      </label>
                      <div className="flex gap-2">
                        <select
                          value={editBreak.start_time.split(':')[0]}
                          onChange={(e) => handleTimeChange(index, 'start', 'hour', e.target.value)}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          {/* 営業時間 11時〜21時のみ */}
                          {Array.from({ length: 11 }, (_, i) => i + 11).map(hour => (
                            <option key={hour} value={String(hour).padStart(2, '0')}>
                              {String(hour).padStart(2, '0')}時
                            </option>
                          ))}
                        </select>
                        <select
                          value={editBreak.start_time.split(':')[1]}
                          onChange={(e) => handleTimeChange(index, 'start', 'minute', e.target.value)}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          {/* 15分単位 */}
                          {['00', '15', '30', '45'].map(minute => (
                            <option key={minute} value={minute}>
                              {minute}分
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        終了時刻
                      </label>
                      <div className="flex gap-2">
                        <select
                          value={editBreak.end_time.split(':')[0]}
                          onChange={(e) => handleTimeChange(index, 'end', 'hour', e.target.value)}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          {/* 営業時間 11時〜21時のみ */}
                          {Array.from({ length: 11 }, (_, i) => i + 11).map(hour => (
                            <option key={hour} value={String(hour).padStart(2, '0')}>
                              {String(hour).padStart(2, '0')}時
                            </option>
                          ))}
                        </select>
                        <select
                          value={editBreak.end_time.split(':')[1]}
                          onChange={(e) => handleTimeChange(index, 'end', 'minute', e.target.value)}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          {/* 15分単位 */}
                          {['00', '15', '30', '45'].map(minute => (
                            <option key={minute} value={minute}>
                              {minute}分
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* 中抜け時間の詳細表示 */}
                  <div className="mt-2 text-sm text-gray-600">
                    {editBreak.start_time} ～ {editBreak.end_time}
                  </div>
                </div>
              ))}
            </div>
          )}
          
          <button
            onClick={() => addBreak()}
            className="mt-3 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center gap-2"
          >
            <Plus size={20} />
            中抜け時間を追加
          </button>
        </div>

        {/* 合計中抜け時間 */}
        {breaks.length > 0 && (
          <div className="mb-6 p-4 bg-blue-50 rounded-lg">
            <div className="flex items-center justify-between">
              <span className="font-semibold text-blue-900">合計中抜け時間:</span>
              <span className="text-xl font-bold text-blue-600">
                {(() => {
                  let totalMinutes = 0;
                  breaks.forEach(b => {
                    const [startHour, startMin] = b.start_time.split(':').map(Number);
                    const [endHour, endMin] = b.end_time.split(':').map(Number);
                    const duration = (endHour * 60 + endMin) - (startHour * 60 + startMin);
                    if (duration > 0) totalMinutes += duration;
                  });
                  const hours = Math.floor(totalMinutes / 60);
                  const minutes = totalMinutes % 60;
                  if (hours === 0) return `${minutes}分`;
                  if (minutes === 0) return `${hours}時間`;
                  return `${hours}時間${minutes}分`;
                })()}
              </span>
            </div>
          </div>
        )}

        {/* アクションボタン */}
        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-6 py-2 text-gray-600 hover:text-gray-800"
          >
            キャンセル
          </button>
          <button
            onClick={handleSave}
            disabled={loading || breaks.length === 0}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <Save size={20} />
            保存
          </button>
        </div>
      </div>
    </div>
  );
};

export default BreakTimeEditorV2;